import { useState, useEffect } from "react";

// Derives the initially required invoice/credit-note IDs from a statement object.
// Expects a shape like: { invoiceGroups: [...], creditTransactionIds: [...] }
export const useSelectedInvoiceIds = (statement) => {
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(null);

  // Initialize selectedInvoiceIds with required IDs when payment request data loads
  useEffect(() => {
    if (!statement?.invoiceGroups || selectedInvoiceIds !== null) return;

    const requiredIds = [
      ...(statement.creditTransactionIds || []),
      ...statement.invoiceGroups
        .filter((g) => g.required)
        .flatMap((g) => [
          ...g.invoices.filter((i) => !i.isDisputed).map((i) => i.ledgerTransactionId),
          ...g.creditNotes.map((cn) => cn.ledgerTransactionId),
          ...g.invoices
            .filter((i) => !i.isDisputed)
            .map((i) => [
              i.ledgerTransactionId,
              ...(i.creditNotes?.map((cn) => cn.ledgerTransactionId) || []),
            ])
            .flat(),
        ]),
    ];

    setSelectedInvoiceIds(new Set(requiredIds));
  }, [statement]);

  // Remove any disputed invoices from selection whenever the statement refreshes
  useEffect(() => {
    if (!statement?.invoiceGroups || selectedInvoiceIds === null) return;

    const disputedIds = new Set(
      statement.invoiceGroups
        .flatMap((g) => g.invoices)
        .filter((i) => i.isDisputed)
        .map((i) => i.ledgerTransactionId)
    );

    if (disputedIds.size === 0) return;

    setSelectedInvoiceIds((current) => {
      if (!current) return current;
      const hasDisputed = [...disputedIds].some((id) => current.has(id));
      if (!hasDisputed) return current;
      const next = new Set(current);
      disputedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [statement]);

  return [selectedInvoiceIds, setSelectedInvoiceIds];
};
